import { MediaType } from '@src/types/Filters';
import { LabelPill } from '@src/components/LabelPill';
import { LocalizedStrings } from '@src/utils/localizedStrings';
import { PerfectScrollbar } from '@src/components/PerfectScrollbar';
import { style } from 'typestyle';
import { CollectionItem } from '@src/fragments/Filters/Search/FilterItem/CollectionItem';
import { RightRequestMQLStatus } from '@adalong/schemas';
import { RightRequestStatusItem } from '@src/fragments/Filters/Search/FilterItem/RightRequestStatusItem';
import { DateItem } from '../Search/FilterItem/DateItem';
import { MediaTypeItem } from '../Search/FilterItem/MediaTypeItem';
import { LanguageItem } from '../Search/FilterItem/LanguageItem';
import { SourceItem } from '../Search/FilterItem/SourceItem';
import { CreatorItem } from '../Search/FilterItem/CreatorItem';
import { FilterNote } from '../FilterNote';
import { ProductItem } from '../Search/FilterItem/ProductItem';
import { useAtomValue } from 'jotai';
import {
  collectionExcludeState,
  creatorExcludeState,
  languageExcludeState,
  ownContentExcludeState,
  productExactExcludeState,
  productSimilarExcludeState,
  creatorCountriesExcludeState,
  getFilter,
  allProducts,
} from '@src/pages/Discover/components/Selectors/Selectors';
import { getSelectedSuggestion } from '../Search/Suggestions/SourcesSuggestion';
import {
  filterCollectionXclusionState,
  filterCreatorCountriesXclusionState,
  filterCreatorXclusionState,
  filterOwnContentXclusionState,
  filterProductExactXclusionState,
  filterProductSimilarXclusionState,
} from '@src/pages/Discover/components/Atoms/Atoms';
import { XclusionUtility } from '../managers/XclusionUtility';
import { addProductNameToSuggestions } from '../Search/Suggestions/ProductsSuggestion';
import {
  clearFilter,
  onFilterChange,
  validMediaType,
  validRightRequestStatus,
} from '../utils/FilterStateUtility';
import { isInParam } from '../Search/Suggestions/CollectionSuggestion';
import { useQueryCollections } from '@src/queries/collections/useQueryCollections';
import { isMySelectionSectionFilled } from '@src/utils/QS';
import { useFiltersParams } from '@src/hooks/useFiltersParams';
import { getAudioFilterSelections } from './audioFilter';
import { LanguageSearch } from '@src/fragments/Filters/managers/LanguageSearch';
import { validAudioLanguageCodes } from '@src/fragments/Filters/utils/AudioLanguage';

export function FilterSelections() {
  const filterState = useAtomValue(getFilter);
  const { filters, setFilters } = useFiltersParams();
  const products = useAtomValue(allProducts);

  const languageInclusion = !useAtomValue(languageExcludeState);
  const creatorInclusion = !useAtomValue(creatorExcludeState);
  const collectionInclusion = !useAtomValue(collectionExcludeState);
  const productExactInclusion = !useAtomValue(productExactExcludeState);
  const productSimilarInclusion = !useAtomValue(productSimilarExcludeState);
  const ownContentInclusion = !useAtomValue(ownContentExcludeState);
  const creatorCountriesInclusion = !useAtomValue(creatorCountriesExcludeState);

  const filterProductExactXclusion = useAtomValue(filterProductExactXclusionState);
  const filterCollectionXclusion = useAtomValue(filterCollectionXclusionState);
  const filterProductSimilarXclusion = useAtomValue(filterProductSimilarXclusionState);
  const filterCreatorXclusion = useAtomValue(filterCreatorXclusionState);
  const filterOwnContentXclusion = useAtomValue(filterOwnContentXclusionState);
  const filterCreatorCountriesXclusion = useAtomValue(filterCreatorCountriesXclusionState);

  const { data: userCollections = [] } = useQueryCollections();
  const empty = !isMySelectionSectionFilled(filterState);

  const selected = {
    date: () => filters.date[0],
    source: () => getSelectedSuggestion(filters.source),
    ownContent: () => getSelectedSuggestion(filters.ownContent),
    lang: () => filters.lang.map((val) => ({ key: val, display: XclusionUtility.getRawKey(val) })),
    creators: () =>
      filters.creator.map((value) => ({
        key: value,
        display: `${XclusionUtility.getRawKey(value)}`,
      })),
    productsExact: () => {
      const selectedProductsIds = filterState.productExact.map((p) => ({
        key: p,
        display: XclusionUtility.getRawKey(p),
      }));
      return addProductNameToSuggestions(selectedProductsIds, products);
    },
    productsSimilar: () => {
      const selectedProductsIds = filterState.productSimilar.map((p) => ({
        key: p,
        display: XclusionUtility.getRawKey(p),
      }));
      return addProductNameToSuggestions(selectedProductsIds, products);
    },
    mediaType: () => filters.media.map((m) => validMediaType(m)).filter((m): m is MediaType => !!m),
    audio: () => getAudioFilterSelections(filters.audio),
    audioLang: () => {
      const validCodes = new Set(validAudioLanguageCodes(filters.audioLang.map((value) => XclusionUtility.getRawKey(value))));
      return filters.audioLang
        .filter((value) => validCodes.has(XclusionUtility.getRawKey(value)))
        .map((value) => ({
          key: value,
          display: LanguageSearch.translateIso6393(XclusionUtility.getRawKey(value)),
        }));
    },
    rightRequestStatus: () =>
      filters.rrStatus
        .map((m) => validRightRequestStatus(m))
        .filter((m): m is RightRequestMQLStatus => !!m),
    collection: () => {
      const rawCollectionFilter = XclusionUtility.getRawKeys(filters.collection || []);
      const allCollectionDescs = userCollections.map(({ _id, name }) => ({
        key: _id,
        display: name,
      }));
      return allCollectionDescs
        .filter(({ key }) => isInParam(key, rawCollectionFilter))
        .map((val) => ({
          key: XclusionUtility.xcludeKey(val.key, filterCollectionXclusion),
          display: val.display,
        }));
    },
    productCatalogCountries: () =>
      filters.productCatalogCountries.map((value) => ({
        key: value,
        display: XclusionUtility.getRawKey(value),
      })),
    creatorCountries: () => getSelectedSuggestion(filters.creatorCountries),
  };

  const date = selected.date();

  const onRemove = {
    date: () => clearFilter(setFilters, 'date'),
    mediaType: (mediaType: MediaType) => onFilterChange(setFilters, 'media')([mediaType], false),
    rightRequestStatus: (rightRequestStatus: RightRequestMQLStatus) =>
      onFilterChange(setFilters, 'rrStatus')([rightRequestStatus], false),
    audio: () => clearFilter(setFilters, 'audio'),
    audioLang: (key: string) => onFilterChange(setFilters, 'audioLang')([key], false),
    languages: (key: string) => onFilterChange(setFilters, 'lang')([key], false),
    sources: (key: string) => onFilterChange(setFilters, 'source')([key], false),
    creators: (key: string) =>
      onFilterChange(setFilters, 'creator')([key], false, filterCreatorXclusion),
    productsExact: (key: string) =>
      onFilterChange(setFilters, 'productExact')([key], false, filterProductExactXclusion),
    productsSimilar: (key: string) =>
      onFilterChange(setFilters, 'productSimilar')([key], false, filterProductSimilarXclusion),
    collection: (key: string) =>
      onFilterChange(setFilters, 'collection')([key], false, filterCollectionXclusion),
    ownContent: (key: string) =>
      onFilterChange(setFilters, 'ownContent')([key], false, filterOwnContentXclusion),
    productCatalogCountries: (key: string) =>
      onFilterChange(setFilters, 'productCatalogCountries')([key], false),
    creatorCountries: (key: string) =>
      onFilterChange(setFilters, 'creatorCountries')([key], false, filterCreatorCountriesXclusion),
  };

  return (
    <PerfectScrollbar
      options={{
        wheelSpeed: 0.6,
      }}
      className={selectionClass}
    >
      {empty ? (
        <FilterNote>
          <i>{lang.empty}</i>
        </FilterNote>
      ) : null}
      {date ? (
        <LabelPill onRemove={() => onRemove.date()}>
          <DateItem date={date} />
        </LabelPill>
      ) : null}
      {selected.rightRequestStatus().map((rightRequestStatus: RightRequestMQLStatus) => (
        <LabelPill
          key={rightRequestStatus}
          onRemove={() => onRemove.rightRequestStatus(rightRequestStatus)}
        >
          <RightRequestStatusItem rrStatus={rightRequestStatus} />
        </LabelPill>
      ))}
      {selected.mediaType().map((mediaType) => (
        <LabelPill key={mediaType} onRemove={() => onRemove.mediaType(mediaType)}>
          <MediaTypeItem type={mediaType} />
        </LabelPill>
      ))}
      {selected.audio().map((audio) => (
        <LabelPill key={audio.key} onRemove={() => onRemove.audio()}>
          <span>{audio.display}</span>
        </LabelPill>
      ))}
      {selected.audioLang().map((audioLanguage) => (
        <LabelPill key={audioLanguage.key} onRemove={() => onRemove.audioLang(audioLanguage.key)}>
          <LanguageItem suggestion={audioLanguage} />
        </LabelPill>
      ))}
      {selected.lang().map((language) => (
        <LabelPill
          variant={languageInclusion ? 'positive' : 'negative'}
          onRemove={() => onRemove.languages(language.key)}
          key={language.key}
        >
          <LanguageItem suggestion={language} />
        </LabelPill>
      ))}
      {selected.source().map((source) => (
        <LabelPill onRemove={() => onRemove.sources(source.key)} key={source.key}>
          <SourceItem suggestion={source} />
        </LabelPill>
      ))}
      {selected.creators().map((creator) => (
        <LabelPill
          variant={creatorInclusion ? 'positive' : 'negative'}
          onRemove={() => onRemove.creators(creator.key)}
          key={creator.key}
        >
          <CreatorItem suggestion={creator} />
        </LabelPill>
      ))}
      {selected.productsExact().map((product) => (
        <LabelPill
          variant={productExactInclusion ? 'positive' : 'negative'}
          onRemove={() => onRemove.productsExact(product.key)}
          key={product.key}
        >
          <ProductItem suggestion={product} />
        </LabelPill>
      ))}
      {selected.productsSimilar().map((product) => (
        <LabelPill
          variant={productSimilarInclusion ? 'positive' : 'negative'}
          onRemove={() => onRemove.productsSimilar(product.key)}
          key={product.key}
        >
          <ProductItem suggestion={product} />
        </LabelPill>
      ))}
      {selected.ownContent().map((ownContent) => (
        <LabelPill
          variant={ownContentInclusion ? 'positive' : 'negative'}
          onRemove={() => onRemove.ownContent(ownContent.key)}
          key={ownContent.key}
        >
          <SourceItem suggestion={ownContent} />
        </LabelPill>
      ))}
      {selected.collection().map((collection) => (
        <LabelPill
          variant={collectionInclusion ? 'positive' : 'negative'}
          onRemove={() => onRemove.collection(collection.key)}
          key={collection.key}
        >
          <CollectionItem suggestion={collection} />
        </LabelPill>
      ))}
      {selected.productCatalogCountries().map((country) => (
        <LabelPill
          onRemove={() => onRemove.productCatalogCountries(country.key)}
          key={country.key}
        >
          <span>{country.display}</span>
        </LabelPill>
      ))}
      {selected.creatorCountries().map((country) => (
        <LabelPill
          variant={creatorCountriesInclusion ? 'positive' : 'negative'}
          onRemove={() => onRemove.creatorCountries(country.key)}
          key={country.key}
        >
          <span>{country.key}</span>
        </LabelPill>
      ))}
    </PerfectScrollbar>
  );
}

const lang = LocalizedStrings({
  en: {
    empty: 'No filters applied',
  },
});

const selectionClass = style({
  maxHeight: 145,
});
